import { Promise } from 'bluebird';
import { useCallback, useMemo, useState } from 'react';
import { defaultApiFetchErrorHandler } from 'react-scale/lib/api-fetch';
import { promoteToTopLevelManager, removeWorkerEntries } from '../ExpertManagementPage/api';

export function useExpertActionsState(data: object[] | null, reloadData: () => void) {
  const [rowSelectionState, setRowSelectionState] = useState<{ [key: string]: boolean }>({});
  const [moveManagersModalOpen, setMoveManagersModalOpen] = useState<boolean>(false);
  const [isModifying, setIsModifying] = useState<boolean>(false);

  const moveToTopLevel = useCallback(
    async (id: any) => {
      const result = await promoteToTopLevelManager(id);
      if (result.success) {
        reloadData();
      } else {
        defaultApiFetchErrorHandler({ name: 'error', message: result.error, logError: () => null });
      }
    },
    [reloadData],
  );

  const selectedRows = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.filter((_row: any, index: number) => rowSelectionState[index]);
  }, [data, rowSelectionState]);

  const handleUnfollowUsers = useCallback(async () => {
    if (selectedRows.length === 0) {
      return;
    }
    const selectedWorkerIds = selectedRows.map((row: any) => row.id);
    if (
      !window.confirm(
        'Unfollowing users will remove them from being tracked. You will be able to add them back at any time in the expert management tab. Do you wish to continue?',
      )
    ) {
      return;
    }
    setIsModifying(true);
    try {
      await removeWorkerEntries(selectedWorkerIds);
      reloadData();
    } finally {
      setIsModifying(false);
    }
  }, [selectedRows, reloadData]);

  const handleSetManagerToNull = useCallback(async () => {
    if (selectedRows.length === 0) {
      return;
    }
    const selectedWorkerIds = selectedRows.map((row: any) => row.id);
    if (
      !window.confirm(
        'Moving leaders to the top level will remove them from their current team leader. Do you wish to continue?',
      )
    ) {
      return;
    }
    setIsModifying(true);
    try {
      await Promise.map(selectedWorkerIds, workerId => moveToTopLevel(workerId));
      reloadData();
    } finally {
      setIsModifying(false);
    }
  }, [selectedRows, reloadData, moveToTopLevel]);

  const handleAssignNewManagers = useCallback(() => {
    setMoveManagersModalOpen(true);
  }, [setMoveManagersModalOpen]);

  return {
    rowSelectionState,
    setRowSelectionState,
    selectedRows,
    moveManagersModalOpen,
    setMoveManagersModalOpen,
    handleUnfollowUsers,
    handleAssignNewManagers,
    handleSetManagerToNull,
    isModifying,
  };
}
